import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { projectPointOntoWallAxisCartesianAware } from '@geometry'
import { elementTypes } from '@scene'
import { v4 as uuid } from 'uuid'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button, Divider, IconButton, Stack, Typography } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useMutation } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useElementSelectionStore, useModelStore } from '@editorStores'
import {
  useElementLabel,
  useFloorplanClickListener,
  useFloorplanElementTypeToSelectable,
  useModelClickListeners,
  useResultsInvalidation,
  useSelectionMode,
  useTypeInteraction,
} from '@editorHooks'
import { useModelInvalidation, useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { createOpening } from '@mutations'

interface Props {
  sourceWall: ShapeObject
  opening: Opening
}

const MODE_KEY = 'copy-opening-to-walls'

const wallTypeInteraction: ElementTypes[] = ['inner_walls', 'outer_walls']

const WallOpeningCopyForm = ({ sourceWall, opening }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const { projectId } = useParams()
  const invalidateResults = useResultsInvalidation()
  const invalidateModel = useModelInvalidation()
  const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()
  const getLabel = useElementLabel()
  const walls = useModelStore(state => state.model.walls)
  const setHighlightedIds = useElementSelectionStore(state => state.setHighlightedIds)
  const resetHighlightedIds = useElementSelectionStore(state => state.resetHighlightedIds)
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)
  const {
    actions: { setElements },
  } = useStructuralPlanningSelection()

  const [targetWallGuids, setTargetWallGuids] = useState<string[]>([])

  const wallGuids = useMemo(() => new Set(walls.map(w => w.guid)), [walls])
  const isSelectingCopyTargets = selectionModeKey === MODE_KEY

  useTypeInteraction(wallTypeInteraction, isSelectingCopyTargets)

  const floorplanElementTypeToSelectable = useMemo(() => {
    if (!isSelectingCopyTargets) return null

    return elementTypes.reduce((acc, type) => {
      acc[type] = type === 'inner_walls' || type === 'outer_walls'
      return acc
    }, {} as Partial<Record<ElementTypes, boolean>>)
  }, [isSelectingCopyTargets])

  useFloorplanElementTypeToSelectable(floorplanElementTypeToSelectable)

  const handleCopyWallSelectByGuid = useCallback(
    (wallGuid: string) => {
      if (!wallGuids.has(wallGuid) || wallGuid === sourceWall.guid) return
      setTargetWallGuids(prev => (prev.includes(wallGuid) ? prev : [...prev, wallGuid]))
    },
    [wallGuids, sourceWall.guid],
  )

  const handleCopyWallSelect = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const { elementType } = event.object.userData as { elementType?: string }
      if (elementType !== 'inner_walls' && elementType !== 'outer_walls') return
      handleCopyWallSelectByGuid(event.object.name)
    },
    [handleCopyWallSelectByGuid],
  )

  useModelClickListeners(
    handleCopyWallSelect,
    [handleCopyWallSelect],
    isSelectingCopyTargets,
    true,
    false,
    true,
  )

  useFloorplanClickListener(handleCopyWallSelectByGuid, {
    enabled: isSelectingCopyTargets,
  })

  const { mutateAsync: copyOpeningsToWalls, isLoading: isCopying } = useMutation(
    async (targets: string[]) => {
      if (!projectId || !opening) return

      const sourceMinZ = Math.min(...sourceWall.shape.points.map(p => p.z))

      for (const targetGuid of targets) {
        const targetWall = walls.find(w => w.guid === targetGuid)
        if (!targetWall) continue

        const targetMinZ = Math.min(...targetWall.shape.points.map(p => p.z))
        const deltaZ = targetMinZ - sourceMinZ

        const shiftedPoints = opening.shape.points.map(
          p => new ImmutableVector3(p.x, p.y, p.z + deltaZ),
        )

        const projectedPoints = shiftedPoints.map(p =>
          projectPointOntoWallAxisCartesianAware(targetWall, p),
        )

        const newGuid = uuid()

        await createOpening.request(projectId, {
          element_guid: targetWall.guid,
          shape: {
            guid: newGuid,
            points: projectedPoints,
          },
        })
      }

      setElements([])
      await invalidateModel(projectId)
      invalidateResults(projectId)

      enqueueSnackbar('Öffnung erfolgreich auf andere Wände kopiert', { variant: 'success' })
    },
  )

  useEffect(() => {
    setHighlightedIds(targetWallGuids)
    return () => resetHighlightedIds()
  }, [targetWallGuids, setHighlightedIds, resetHighlightedIds])

  return (
    <>
      <Divider textAlign="center" sx={{ width: '95%' }}>
        {'Öffnung kopieren'}
      </Divider>
      <Stack direction="column" spacing={1} width="100%">
        <Stack direction="column" spacing={1} width="100%" alignItems="center">
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Button
              size="small"
              variant={isSelectingCopyTargets ? 'contained' : 'outlined'}
              onClick={() => {
                if (isSelectingCopyTargets) {
                  unsetSelectionMode()
                } else {
                  setProblemViewActiveErrors(false)
                  setProblemViewActiveWarnings(false)
                  setTargetWallGuids([])
                  setSelectionMode({
                    key: MODE_KEY,
                    message:
                      'Klicken Sie im Modell auf eine oder mehrere Wände, auf die die Öffnung kopiert werden soll',
                  })
                }
              }}
              disabled={isCopying}
              color={'info'}
            >
              Wände auswählen
            </Button>
            <Button
              size="small"
              onClick={() => {
                setTargetWallGuids([])
                unsetSelectionMode()
              }}
              disabled={isCopying}
              variant="outlined"
            >
              Auswahl aufheben
            </Button>
            <SaveButton
              size="small"
              onClick={async () => {
                await copyOpeningsToWalls(targetWallGuids)
                unsetSelectionMode()
                setProblemViewActiveErrors(problemViewActiveErrors)
                setProblemViewActiveWarnings(problemViewActiveWarnings)
                setTargetWallGuids([])
              }}
              disabled={isCopying || targetWallGuids.length === 0}
            >
              Kopieren
            </SaveButton>
          </Stack>
          {targetWallGuids.length === 0 ? (
            <Typography>Keine Wände ausgewählt</Typography>
          ) : (
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: 1,
                width: '100%',
              }}
            >
              {targetWallGuids.map(guid => (
                <Stack key={guid} direction="row" alignItems="center" spacing={1}>
                  <Typography noWrap align="center">
                    {getLabel(guid)}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Zielwand entfernen"
                    onClick={() => setTargetWallGuids(prev => prev.filter(g => g !== guid))}
                    sx={{ padding: 0 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </>
  )
}

export default WallOpeningCopyForm
